import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Widget } from '@detect/shared';
import { dashboardService } from '../../services/dashboards';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { LoadingSpinner, ErrorState } from '../../components/common';
import { formatRelativeTime } from '../../utils/formatters';

/**
 * One widget = one widget-data POST, auto-refreshed. Renderers are
 * deliberately dependency-free (inline SVG for graphs) — charting
 * libraries can replace them without touching the data contract.
 */

const WIDGET_REFRESH_MS = 60_000;

interface SeriesPoint {
  timestampMs: number;
  value: number;
}

interface SeriesResult {
  series: {
    label?: string;
    resourceId: string;
    resourceName?: string;
    datapoint: string;
  };
  points: SeriesPoint[];
}

export function WidgetCard({
  widget,
  onEdit,
  onRemove,
  canEdit,
  onLayout,
}: {
  widget: Widget;
  onEdit?: () => void;
  onRemove?: () => void;
  canEdit: boolean;
  /** Drag-to-move / resize commit (#10); absent = static card. */
  onLayout?: (position: { x: number; y: number; w: number; h: number }) => void;
}) {
  const { tokenReady } = useAuth();
  const { tenantId } = useTenant();
  /** Drag-zoom window (graph widgets): re-queries widget-data with a
   *  narrower startMs/endMs — the endpoint has taken arbitrary windows
   *  since M8, so zoom is purely client-side state. Auto-refresh pauses
   *  while zoomed (a pinned historical window must not slide). */
  const [zoom, setZoom] = useState<{ startMs: number; endMs: number } | null>(
    null
  );
  const query = useQuery<unknown>({
    queryKey: [
      'tenant',
      tenantId,
      'widget-data',
      widget.id,
      widget.type,
      widget.config,
      zoom,
    ],
    enabled: tokenReady && tenantId !== null,
    refetchInterval: zoom === null ? WIDGET_REFRESH_MS : false,
    queryFn: () =>
      dashboardService.widgetData(tenantId as string, {
        type: widget.type,
        config: widget.config,
        ...(zoom ?? {}),
      }),
  });

  const { x, y, w, h } = widget.position;
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [dragPx, setDragPx] = useState<{ dx: number; dy: number } | null>(null);
  const [resizePx, setResizePx] = useState<{ dx: number; dy: number } | null>(
    null
  );

  /** Grid metrics from the live layout: 48 columns, 14px gaps, 32px
   *  rows (globals.css .widget-grid) — measured, not assumed, so a
   *  future style change cannot silently skew drops. */
  const gridSteps = (): { col: number; row: number } | null => {
    const grid = cardRef.current?.parentElement;
    if (!grid) return null;
    const gap = 14;
    const col = (grid.getBoundingClientRect().width - 47 * gap) / 48 + gap;
    return { col, row: 32 + gap };
  };

  /** Active-drag teardown, also run on unmount so a mid-drag route
   *  change or widget removal cannot leak window listeners (codex
   *  finding 2). pointercancel abandons without committing. */
  const dragCleanupRef = useRef<(() => void) | null>(null);
  useEffect(() => () => dragCleanupRef.current?.(), []);

  const beginDrag = (
    e: React.PointerEvent,
    kind: 'move' | 'resize'
  ): void => {
    if (!canEdit || !onLayout) return;
    if ((e.target as HTMLElement).closest('button')) return;
    e.preventDefault();
    dragCleanupRef.current?.();
    const startX = e.clientX;
    const startY = e.clientY;
    const set = kind === 'move' ? setDragPx : setResizePx;
    const cleanup = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      dragCleanupRef.current = null;
      set(null);
    };
    const onMove = (ev: PointerEvent) =>
      set({ dx: ev.clientX - startX, dy: ev.clientY - startY });
    const onCancel = () => cleanup();
    const onUp = (ev: PointerEvent) => {
      cleanup();
      const steps = gridSteps();
      if (!steps) return;
      const dCols = Math.round((ev.clientX - startX) / steps.col);
      const dRows = Math.round((ev.clientY - startY) / steps.row);
      if (dCols === 0 && dRows === 0) return;
      onLayout(
        kind === 'move'
          ? { x: x + dCols, y: y + dRows, w, h }
          : { x, y, w: w + dCols, h: h + dRows }
      );
    };
    dragCleanupRef.current = cleanup;
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
  };

  return (
    <div
      ref={cardRef}
      className="widget-card"
      // Honor the stored grid contract exactly: explicit column/row
      // start + span, never auto-flow (x+w<=48 is schema-enforced).
      style={{
        gridColumn: `${x + 1} / span ${w}`,
        gridRow: `${y + 1} / span ${h}`,
        ...(dragPx
          ? {
              transform: `translate(${dragPx.dx}px, ${dragPx.dy}px)`,
              zIndex: 5,
              opacity: 0.85,
            }
          : {}),
      }}
    >
      <div
        className={
          canEdit && onLayout ? 'widget-header widget-header-draggable' : 'widget-header'
        }
        onPointerDown={(e) => beginDrag(e, 'move')}
      >
        <h3 className="widget-title">{widget.title}</h3>
        <span className="row-actions">
          {zoom && (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => setZoom(null)}
            >
              Reset zoom
            </button>
          )}
          {canEdit && onEdit && (
            <button type="button" className="btn btn-ghost" onClick={onEdit}>
              Edit
            </button>
          )}
          {canEdit && onRemove && (
            <button type="button" className="btn btn-ghost" onClick={onRemove}>
              Remove
            </button>
          )}
        </span>
      </div>
      <div className="widget-body">
        {query.isLoading ? (
          <LoadingSpinner />
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : (
          <WidgetBody
            widget={widget}
            data={query.data}
            onZoom={(startMs, endMs) => setZoom({ startMs, endMs })}
          />
        )}
      </div>
      {canEdit && onLayout && (
        <div
          className="widget-resize-handle"
          role="presentation"
          onPointerDown={(e) => beginDrag(e, 'resize')}
          style={
            resizePx
              ? { transform: `translate(${resizePx.dx}px, ${resizePx.dy}px)` }
              : undefined
          }
        />
      )}
    </div>
  );
}

function WidgetBody({
  widget,
  data,
  onZoom,
}: {
  widget: Widget;
  data: unknown;
  onZoom: (startMs: number, endMs: number) => void;
}) {
  switch (widget.type) {
    case 'line_graph':
    case 'area_graph':
      return (
        <LineChart
          series={(data as { series: SeriesResult[] }).series}
          area={widget.type === 'area_graph'}
          onZoom={onZoom}
        />
      );
    case 'big_number': {
      const d = data as { value: number | null; unit: string | null };
      return (
        <div className="big-number">
          {d.value === null ? '—' : formatNumber(d.value)}
          {d.unit && <span className="big-number-unit">{d.unit}</span>}
        </div>
      );
    }
    case 'gauge': {
      const d = data as {
        value: number | null;
        min: number;
        max: number;
        warnAt: number | null;
        critAt: number | null;
        unit: string | null;
      };
      const pct =
        d.value === null
          ? 0
          : Math.min(100, Math.max(0, ((d.value - d.min) / (d.max - d.min)) * 100));
      const level =
        d.value !== null && d.critAt !== null && d.value >= d.critAt
          ? 'critical'
          : d.value !== null && d.warnAt !== null && d.value >= d.warnAt
            ? 'warning'
            : 'ok';
      return (
        <div>
          <div className="big-number">
            {d.value === null ? '—' : formatNumber(d.value)}
            {d.unit && <span className="big-number-unit">{d.unit}</span>}
          </div>
          <div className="gauge-track">
            <div className={`gauge-fill gauge-${level}`} style={{ width: `${pct}%` }} />
          </div>
          <div className="text-muted">
            {d.min} – {d.max}
          </div>
        </div>
      );
    }
    case 'table': {
      const d = data as {
        rows: { series: { label?: string; resourceId: string; datapoint: string }; value: number | null }[];
      };
      return (
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Series</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              {d.rows.map((row, i) => (
                <tr key={i}>
                  <td>{row.series.label ?? `${row.series.resourceId}/${row.series.datapoint}`}</td>
                  <td>{row.value === null ? '—' : formatNumber(row.value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    case 'alert_list': {
      const d = data as {
        alerts: {
          id: string;
          severity: string;
          resourceName: string;
          sensorKey: string;
          datapoint: string;
          lastOccurredAt: string;
        }[];
      };
      if (d.alerts.length === 0) return <p className="text-muted">No matching alerts.</p>;
      return (
        <ul className="widget-alert-list">
          {d.alerts.map((a) => (
            <li key={a.id}>
              <span className={`severity-badge severity-${a.severity}`}>{a.severity}</span>{' '}
              {a.resourceName}: {a.sensorKey}/{a.datapoint}{' '}
              <span className="text-muted">{formatRelativeTime(a.lastOccurredAt)}</span>
            </li>
          ))}
        </ul>
      );
    }
    case 'noc_status': {
      const d = data as {
        resources: { id: string; name: string; worstSeverity: string | null }[];
      };
      if (d.resources.length === 0) {
        return <p className="text-muted">No resources selected.</p>;
      }
      return (
        <div className="noc-grid">
          {d.resources.map((r) => (
            <div
              key={r.id}
              className={`noc-cell noc-${r.worstSeverity ?? 'ok'}`}
              title={r.name}
            >
              {r.name}
            </div>
          ))}
        </div>
      );
    }
    case 'sla': {
      const d = data as {
        windowDays: number;
        rows: { resourceId: string; uptimePct: number; targetPct: number; met: boolean }[];
      };
      return (
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Resource</th>
                <th>Uptime ({d.windowDays}d)</th>
                <th>Target</th>
                <th>Met</th>
              </tr>
            </thead>
            <tbody>
              {d.rows.map((row) => (
                <tr key={row.resourceId}>
                  <td>{row.resourceId}</td>
                  <td>{row.uptimePct}%</td>
                  <td>{row.targetPct}%</td>
                  <td>{row.met ? '✓' : '✗'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    case 'website_status': {
      const d = data as {
        websites: { id: string; name: string; worstSeverity: string | null }[];
      };
      return (
        <div className="noc-grid">
          {d.websites.map((w) => (
            <div key={w.id} className={`noc-cell noc-${w.worstSeverity ?? 'ok'}`}>
              {w.name}
            </div>
          ))}
        </div>
      );
    }
    case 'text':
      // Plain text rendering only — never HTML injection.
      return (
        <p className="widget-text">{String(widget.config.markdown ?? '')}</p>
      );
    default:
      return <p className="text-muted">Unsupported widget type.</p>;
  }
}

function formatNumber(value: number): string {
  if (Math.abs(value) >= 1000) return value.toLocaleString();
  return String(Math.round(value * 100) / 100);
}

/** Minimum drag width (viewBox units) that counts as a zoom rather
 *  than a sloppy click. */
const ZOOM_MIN_DRAG = 8;

function LineChart({
  series,
  area,
  onZoom,
}: {
  series: SeriesResult[];
  area: boolean;
  onZoom: (startMs: number, endMs: number) => void;
}) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  /** Legend toggling — keyed by ORIGINAL series index so colors stay
   *  stable while individual series are hidden. */
  const [hidden, setHidden] = useState<ReadonlySet<number>>(new Set());
  const [hoverX, setHoverX] = useState<number | null>(null);
  const [dragStart, setDragStart] = useState<number | null>(null);

  const visible = series
    .map((s, i) => ({ s, i }))
    .filter(({ i }) => !hidden.has(i));
  const all = visible.flatMap(({ s }) => s.points);

  const W = 600;
  const H = 160;
  const PAD = 6;
  // Scales derive from VISIBLE series only, so hiding an outlier
  // series rescales the rest (the point of legend toggling).
  const minT = all.length ? Math.min(...all.map((p) => p.timestampMs)) : 0;
  const maxT = all.length ? Math.max(...all.map((p) => p.timestampMs)) : 0;
  const minV = all.length ? Math.min(...all.map((p) => p.value)) : 0;
  const maxV = all.length ? Math.max(...all.map((p) => p.value)) : 0;
  const x = (t: number) =>
    maxT === minT ? W / 2 : PAD + ((t - minT) / (maxT - minT)) * (W - 2 * PAD);
  const y = (v: number) =>
    maxV === minV ? H / 2 : H - PAD - ((v - minV) / (maxV - minV)) * (H - 2 * PAD);
  const xToTime = (px: number) =>
    maxT === minT
      ? minT
      : minT + ((px - PAD) / (W - 2 * PAD)) * (maxT - minT);
  const COLORS = ['#2563eb', '#059669', '#d97706', '#dc2626', '#7c3aed'];

  /** Pointer position in viewBox coordinates (the svg is responsive,
   *  so client px must be rescaled by the rendered width). */
  const toViewX = (e: React.PointerEvent<SVGSVGElement>): number => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return 0;
    return ((e.clientX - rect.left) / rect.width) * W;
  };

  /** Nearest point per visible series at the hovered time. */
  const readout =
    hoverX === null || all.length === 0
      ? null
      : (() => {
          const t = xToTime(hoverX);
          const rows = visible
            .map(({ s, i }) => {
              if (s.points.length === 0) return null;
              const nearest = s.points.reduce((best, p) =>
                Math.abs(p.timestampMs - t) < Math.abs(best.timestampMs - t)
                  ? p
                  : best
              );
              return { i, label: seriesLabel(s), point: nearest };
            })
            .filter((r) => r !== null);
          return rows.length ? { t, rows } : null;
        })();

  if (series.flatMap((s) => s.points).length === 0) {
    return <p className="text-muted">No data in range.</p>;
  }

  const clearPointerState = () => {
    setHoverX(null);
    setDragStart(null);
  };

  const endDrag = (e: React.PointerEvent<SVGSVGElement>) => {
    // Captured release may land OUTSIDE the svg — clear the crosshair
    // then too, or it stays pinned (codex finding 1).
    const rect = svgRef.current?.getBoundingClientRect();
    const inside =
      rect !== undefined &&
      e.clientX >= rect.left &&
      e.clientX <= rect.right &&
      e.clientY >= rect.top &&
      e.clientY <= rect.bottom;
    if (!inside) setHoverX(null);
    if (dragStart === null) return;
    const end = toViewX(e);
    setDragStart(null);
    if (Math.abs(end - dragStart) < ZOOM_MIN_DRAG || maxT === minT) return;
    const t1 = Math.round(xToTime(Math.min(dragStart, end)));
    const t2 = Math.round(xToTime(Math.max(dragStart, end)));
    if (t2 > t1) onZoom(t1, t2);
  };

  return (
    <div>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        className="line-chart line-chart-interactive"
        role="img"
        aria-label="Time-series chart — drag to zoom"
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          setDragStart(toViewX(e));
        }}
        onPointerMove={(e) => setHoverX(toViewX(e))}
        onPointerUp={endDrag}
        onPointerCancel={clearPointerState}
        onLostPointerCapture={() => setDragStart(null)}
        onPointerLeave={clearPointerState}
      >
        {visible.map(({ s, i }) => {
          const pts = s.points.map((p) => `${x(p.timestampMs)},${y(p.value)}`).join(' ');
          const color = COLORS[i % COLORS.length];
          return (
            <g key={i}>
              {area && s.points.length > 1 && (
                <polygon
                  points={`${x(s.points[0].timestampMs)},${H - PAD} ${pts} ${x(
                    s.points[s.points.length - 1].timestampMs
                  )},${H - PAD}`}
                  fill={color}
                  opacity={0.15}
                />
              )}
              <polyline points={pts} fill="none" stroke={color} strokeWidth={1.5} />
            </g>
          );
        })}
        {dragStart !== null && hoverX !== null && (
          <rect
            x={Math.min(dragStart, hoverX)}
            y={0}
            width={Math.abs(hoverX - dragStart)}
            height={H}
            className="chart-zoom-band"
          />
        )}
        {hoverX !== null && dragStart === null && (
          <line x1={hoverX} y1={0} x2={hoverX} y2={H} className="chart-crosshair" />
        )}
      </svg>
      <div className="chart-legend">
        {series.map((s, i) => {
          // Never hide the LAST visible series — an empty chart reads
          // as "no data", which would be a lie. Non-actionable toggles
          // say so to AT users (codex finding 2).
          const lastVisible =
            !hidden.has(i) && hidden.size >= series.length - 1;
          return (
          <button
            key={i}
            type="button"
            className={
              hidden.has(i)
                ? 'chart-legend-item chart-legend-item-off'
                : 'chart-legend-item'
            }
            aria-pressed={!hidden.has(i)}
            aria-disabled={lastVisible}
            onClick={() => {
              if (lastVisible) return;
              setHidden((prev) => {
                const next = new Set(prev);
                if (next.has(i)) next.delete(i);
                else next.add(i);
                return next;
              });
            }}
          >
            <span
              className="chart-swatch"
              style={{ background: COLORS[i % COLORS.length] }}
            />
            {seriesLabel(s)}
          </button>
          );
        })}
      </div>
      {readout ? (
        <div className="chart-readout">
          <span className="text-muted">
            {new Date(readout.t).toLocaleString()}
          </span>
          {readout.rows.map((r) => (
            <span key={r.i} className="chart-readout-value">
              <span
                className="chart-swatch"
                style={{ background: COLORS[r.i % COLORS.length] }}
              />
              {formatNumber(r.point.value)}
            </span>
          ))}
        </div>
      ) : (
        <div className="text-muted">
          {minV === maxV
            ? `value ${minV}`
            : `range ${formatNumber(minV)} – ${formatNumber(maxV)}`}
        </div>
      )}
    </div>
  );
}

function seriesLabel(s: SeriesResult): string {
  return (
    s.series.label ??
    `${s.series.resourceName ?? s.series.resourceId}/${s.series.datapoint}`
  );
}
